// JavaScript code for acf_blocks/education-programs-booking-system component
(() => {
    "use strict";

    document.querySelectorAll('.acf-blocks-education-programs-booking-system').forEach((block) => {
        const target = block.querySelector('[data-booking-target]');
        if (!target) return;

        const ajaxUrl = block.dataset.ajaxUrl;
        const nonce   = block.dataset.nonce;

        // Guards against out-of-order AJAX responses: calendar month nav / date clicks each fire
        // their own request, so a quick run of clicks can have two in flight at once. Only the
        // response matching the CURRENT latest request may render.
        let latestRequestId = 0;

        // Lives outside [data-booking-target] (a sibling in component.php), so it survives every
        // innerHTML swap the wizard steps make.
        const processingModal = block.querySelector('[data-booking-processing-modal]');

        function setLoading(isLoading, message) {
            target.classList.toggle('is-loading', isLoading);
            target.style.opacity = isLoading ? '0.5' : '';
            target.setAttribute('aria-busy', isLoading ? 'true' : 'false');

            if (processingModal) {
                if (isLoading) {
                    const messageEl = processingModal.querySelector('[data-booking-processing-message]');
                    if (messageEl) messageEl.textContent = message || 'Loading…';
                }
                processingModal.classList.toggle('hidden', !isLoading);
            }
        }

        // Keeps only programme_id + draft in the visible URL after any wizard step - every other
        // param (booking_action, calendar_month, form values) is that request's own business.
        function updateUrlAfterSwap(programmeId, draftToken) {
            const url = new URL(window.location.href);

            if (programmeId) url.searchParams.set('programme_id', programmeId);
            if (draftToken) {
                url.searchParams.set('draft', draftToken);
            } else {
                url.searchParams.delete('draft');
            }

            ['booking_action', 'calendar_month', 'school_year', 'preferred_date', 'preferred_start_time']
                .forEach((field) => url.searchParams.delete(field));

            history.pushState(null, '', url);
        }

        // history.pushState() never re-renders on its own, so Back/Forward would leave the screen
        // out of sync with the URL. Every piece of state lives in the URL or the server-side draft
        // it points at, so a reload of the URL now showing reconstructs the right step.
        window.addEventListener('popstate', () => {
            window.location.reload();
        });

        // Moves focus to the new step's heading after a swap, so keyboard and screen-reader users
        // land at the top of what changed rather than on a detached element. Scrolls the step into
        // view on small screens, where the summary sits above it.
        function focusNewStep() {
            const heading = target.querySelector('h1');
            if (!heading) return;

            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });

            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const top = block.getBoundingClientRect().top + window.scrollY - 100;
            if (window.scrollY > top) {
                window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
            }
        }

        async function submitWizardAction(formData, fallbackHref, message, moveFocus) {
            const requestId = ++latestRequestId;

            formData.append('action', 'sjm_booking_wizard_action');
            formData.append('nonce', nonce);
            formData.append('current_url', window.location.href);

            setLoading(true, message);

            try {
                const response = await fetch(ajaxUrl, { method: 'POST', body: formData });
                const data     = await response.json();

                if (requestId !== latestRequestId) return;

                if (data.success) {
                    target.innerHTML = data.data.html;
                    updateUrlAfterSwap(formData.get('programme_id'), data.data.draft_token);

                    if (window.sjmBookingAnalytics) window.sjmBookingAnalytics.scan(target);
                    if (moveFocus) focusNewStep();
                } else if (fallbackHref) {
                    window.location.href = fallbackHref;
                }
            } catch (_) {
                if (requestId === latestRequestId && fallbackHref) {
                    window.location.href = fallbackHref;
                }
            } finally {
                if (requestId === latestRequestId) {
                    setLoading(false);
                }
            }
        }

        // Booking summary collapse/expand. One toggle drives two panels:
        //   - [data-booking-summary-description] (description + tags) - collapsible at every size.
        //   - [data-booking-summary-details] (figures, total, trust badge) - only collapsible below
        //     `lg`; its `lg:contents` keeps it always visible on desktop.
        function setSummaryExpanded(toggle, isExpanded) {
            const summary = toggle.closest('[data-booking-summary]');
            if (!summary) return;

            toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

            summary.querySelectorAll('[data-booking-summary-description], [data-booking-summary-details]').forEach((panel) => {
                panel.classList.toggle('hidden', !isExpanded);
                panel.classList.toggle('flex', isExpanded);
            });

            const chevron = toggle.querySelector('[data-booking-summary-chevron]');
            if (chevron) chevron.classList.toggle('rotate-180', isExpanded);
        }

        target.addEventListener('click', (e) => {
            const toggle = e.target.closest('[data-booking-summary-toggle]');
            if (toggle && target.contains(toggle)) {
                setSummaryExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true');
                return;
            }

            const openToggle = target.querySelector('[data-booking-summary-toggle][aria-expanded="true"]');
            if (openToggle && !openToggle.closest('[data-booking-summary]').contains(e.target)) {
                setSummaryExpanded(openToggle, false);
            }
        });

        target.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;

            const openToggle = target.querySelector('[data-booking-summary-toggle][aria-expanded="true"]');
            if (openToggle) {
                setSummaryExpanded(openToggle, false);
                openToggle.focus();
            }
        });

        // In-wizard links (calendar month nav, a calendar date, Back, "None of these dates work")
        // carry their own state in the href. A calendar link lives inside Step 2's form, so the
        // request starts from that form's current values (e.g. a start time already picked), with
        // the link's own query values applied on top. booking_action is reset first - the form's
        // hidden field would otherwise turn every calendar click into a full step submit.
        target.addEventListener('click', async (e) => {
            const link = e.target.closest('[data-booking-calendar-nav], [data-booking-calendar-date], [data-booking-back]');
            if (!link || !target.contains(link)) return;

            e.preventDefault();

            const url      = new URL(link.href);
            const form     = link.closest('form');
            const formData = form ? new FormData(form) : new FormData();

            formData.set('booking_action', '');
            url.searchParams.forEach((value, key) => formData.set(key, value));

            const bookingAction = url.searchParams.get('booking_action') || '';
            const isNavigation  = ['back_to_details', 'back_to_calendar', 'dates_unavailable'].includes(bookingAction);
            const message       = ['back_to_details', 'dates_unavailable'].includes(bookingAction) ? 'Loading…' : 'Checking availability';

            await submitWizardAction(formData, link.href, message, isNavigation);

            // A date pick re-renders the calendar - keep focus on the picked date, not the page top.
            if (link.matches('[data-booking-calendar-date]')) {
                const picked = target.querySelector(`[data-booking-calendar-date][data-date="${link.dataset.date}"]`);
                if (picked) picked.focus();
            }
        });

        // ---- Client-side validation (Steps 1 and the dates-don't-work step) ----
        // Mirrors logic.php's validators so errors appear next to each field before anything is
        // sent. The forms are `novalidate` (native `required` would block this flow and show the
        // browser's own tooltips). Server-side validation stays authoritative regardless.
        const EMAIL_TYPO_DOMAINS = ['gmial', 'gmal', 'gamil', 'gmil', 'hotnail', 'hotmai', 'hotmial', 'outlok', 'outllok', 'yaho'];

        const MESSAGES = {
            lead_first_name: 'Please enter your first name.',
            lead_last_name: 'Please enter your last name.',
            school_name: 'Please enter your school name.',
            lead_job_title: 'Please enter your job title.',
            lead_email: 'Please enter an email address.',
            lead_phone: 'Please enter a phone number.',
            hod_name: 'Please enter a name.',
            hod_email: 'Please enter an email address.',
            accounts_name: 'Please enter a name.',
            accounts_email: 'Please enter an email address.',
            num_students: 'Please enter at least 1 student.',
            num_teachers: 'Please enter the number of teachers (0 or more).',
            year_level: 'Please select a year level.',
            alt_preferred_dates: 'Please tell us which dates or weeks suit you.',
        };

        function fieldErrorFor(input) {
            const key = input.dataset.errorKey;
            return key ? target.querySelector(`[data-field-error="${key}"]`) : null;
        }

        function showFieldError(input, message) {
            const errorEl = fieldErrorFor(input);
            if (errorEl) {
                errorEl.textContent = message;
                errorEl.classList.remove('hidden');
            }

            input.setAttribute('aria-invalid', 'true');
            input.classList.remove('border-black/15');
            input.classList.add('border-red-600');
        }

        function clearFieldError(input) {
            const errorEl = fieldErrorFor(input);
            if (errorEl) errorEl.classList.add('hidden');

            input.removeAttribute('aria-invalid');
            if (input.classList.contains('border-red-600')) {
                input.classList.remove('border-red-600');
                input.classList.add('border-black/15');
            }
        }

        function emailError(value) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';

            const domainName = value.split('@').pop().split('.')[0].toLowerCase();
            return EMAIL_TYPO_DOMAINS.includes(domainName) ? 'Please check the spelling of your email address.' : null;
        }

        // Renato's note: phone numbers are 8 to 20 digits (spaces, brackets, dots and dashes ignored, a leading + allowed).
        function phoneError(value) {
            return /^\+?\d{8,20}$/.test(value.replace(/[\s().-]/g, '')) ? null : 'Please enter a phone number of 8 to 20 digits.';
        }

        function validateField(input) {
            const rules = (input.dataset.validate || '').split(',').filter(Boolean);
            const value = input.value.trim();
            const key   = input.dataset.errorKey;

            for (const rule of rules) {
                if (rule === 'required' && value === '') return MESSAGES[key] || 'This field is required.';
                if (rule === 'email' && value !== '') {
                    const message = emailError(value);
                    if (message) return message;
                }
                if (rule === 'phone' && value !== '') {
                    const message = phoneError(value);
                    if (message) return message;
                }
                if (rule.startsWith('integer-min:')) {
                    const min = parseInt(rule.split(':')[1], 10);
                    const n   = Number(value);
                    if (value !== '' && (!Number.isInteger(n) || n < min)) return MESSAGES[key] || `Please enter a whole number of ${min} or more.`;
                }
            }

            return null;
        }

        function validateForm(form) {
            let firstInvalid = null;

            form.querySelectorAll('[data-validate]').forEach((input) => {
                clearFieldError(input);
                const message = validateField(input);

                if (message) {
                    showFieldError(input, message);
                    if (!firstInvalid) firstInvalid = input;
                }
            });

            const summary = target.querySelector('[data-booking-error-summary]');
            if (summary) summary.classList.toggle('hidden', !firstInvalid);

            return firstInvalid;
        }

        target.addEventListener('input', (e) => {
            if (e.target.matches('[data-validate]')) clearFieldError(e.target);
        });

        // Every step's form - progressive enhancement over a plain GET submit.
        target.addEventListener('submit', async (e) => {
            const form = e.target.closest('[data-booking-step-form]');
            if (!form || !target.contains(form)) return;

            e.preventDefault();

            const bookingAction = form.dataset.bookingAction;

            if (['submit_details', 'submit_alt'].includes(bookingAction)) {
                const firstInvalid = validateForm(form);
                if (firstInvalid) {
                    firstInvalid.focus();
                    if (window.sjmBookingAnalytics) {
                        window.sjmBookingAnalytics.track('booking_step_invalid', { step: bookingAction === 'submit_details' ? 'details' : 'dates_unavailable' });
                    }
                    return;
                }
            }

            const submitBtn = form.querySelector('[type="submit"]');
            if (submitBtn) submitBtn.disabled = true;

            const message = 'submit_calendar' === bookingAction ? 'Checking availability' : ('confirm_booking' === bookingAction || 'submit_alt' === bookingAction ? 'Sending your request…' : 'Loading…');

            await submitWizardAction(new FormData(form), null, message, true);

            if (submitBtn && document.body.contains(submitBtn)) submitBtn.disabled = false;
        });

        // Cosmetic-only live estimate - recomputes the summary's figures as the visitor types,
        // never authoritative (the real figure is computed server-side on the next submit).
        // Delegated and re-queried on every event: an AJAX swap replaces the whole subtree.
        target.addEventListener('input', (e) => {
            if (e.target.matches('[data-booking-num-students]')) {
                const totalEl    = target.querySelector('[data-booking-summary-total]');
                const studentsEl = target.querySelector('[data-booking-summary-students]');
                const students   = parseInt(e.target.value, 10);

                if (studentsEl) {
                    studentsEl.textContent = Number.isFinite(students) && students > 0 ? String(students) : '—';
                }

                if (totalEl) {
                    const participantCost = parseFloat(totalEl.dataset.participantCost || '0');
                    const minCharge       = parseFloat(totalEl.dataset.minCharge || '0');
                    // per_class and per_hour prices don't depend on the headcount (see sjm_booking_estimated_total()).
                    const perStudent      = !['per_class', 'per_hour'].includes(totalEl.dataset.pricingBasis);
                    let total             = perStudent ? participantCost * (Number.isFinite(students) ? students : 0) : parseFloat(totalEl.dataset.fixedTotal || '0');

                    if (students > 0 && minCharge > 0 && total < minCharge) total = minCharge;

                    totalEl.textContent = '$' + Math.round(total).toLocaleString('en-AU');
                }
            }

            if (e.target.matches('[data-booking-num-teachers]')) {
                const teachersEl = target.querySelector('[data-booking-summary-teachers]');
                const teachers   = parseInt(e.target.value, 10);

                if (teachersEl) {
                    teachersEl.textContent = Number.isFinite(teachers) && teachers >= 0 ? String(teachers) : '—';
                }
            }
        });

        target.addEventListener('change', (e) => {
            if (!e.target.matches('#booking-year-level')) return;

            const yearLevelEl = target.querySelector('[data-booking-summary-year-level]');
            if (yearLevelEl) yearLevelEl.textContent = e.target.value !== '' ? e.target.value : '—';

            // The "Carried over from your programme search" hint no longer applies once changed.
            const carriedHint = target.querySelector('[data-booking-year-carried-hint]');
            if (carriedHint) carriedHint.classList.add('hidden');
        });

        // School name suggestions come from the shared sjm-booking/school-search component.
    });
})();
